import { t } from 'ttag'
import styled from 'styled-components'

import { WHITE, SY40T, SLB50S, PB90T, PB97T, BLACK } from '@h2/components/colors'
import { Flex } from '@h2/components/layout/flex'
import { Link } from '@h2/common/components/link'
import { Text } from '@h2/components/typography/text'
import { formatCurrency } from '@h2/../main/js/actions/assessments/budget'
import { LocationIcon, OccupationsIcon, EqualsIcon } from '@h2/components/icons'

import { fmtCurrency } from '../common/helpers'

const CenterCol = styled.div`
  width: 90%;
`
const LocationLink = styled(Link)`
  color: ${SY40T};
  font-weight: bold;
  font-size: 20px;
  &:hover {
    color: ${PB97T};
  }
`
const IconCol = styled.div`
  margin: ${({ isSelected }) => (isSelected ? '0' : '21px 0 0 0')};
  height: ${({ isSelected }) => (isSelected ? '69px' : '48px')};
  width: ${({ isSelected }) => (isSelected ? '69px' : '49px')};
  background-image: url('/images/common/iconset.png');

  &.occupationicon {
    background-position: -529px -376px;
    height: 33px;
    width: 34px;
    margin: 0 7px 0 0;
    float: left;
  }

  &.earningsicon {
    background-position: ${({ isSelected }) =>
      isSelected ? '-72px -443px' : '-5px -337px'};
  }

  &.housingicon {
    background-position: ${({ isSelected, isCompleted }) =>
      isSelected ? '-142px -443px' : isCompleted ? '-109px -388px' : '-109px -337px'};
  }

  &.utilityicon {
    background-position: ${({ isSelected, isCompleted }) =>
      isSelected ? '-213px -443px' : isCompleted ? '-162px -388px' : '-162px -337px'};
  }

  &.foodicon {
    background-position: ${({ isSelected, isCompleted }) =>
      isSelected ? '-284px -443px' : isCompleted ? '-213px -388px' : '-213px -337px'};
  }

  &.entertainmenticon {
    background-position: ${({ isSelected, isCompleted }) =>
      isSelected ? '-1px -513px' : isCompleted ? '-267px -388px' : '-267px -337px'};
  }

  &.clothesicon {
    background-position: ${({ isSelected, isCompleted }) =>
      isSelected ? '-72px -513px' : isCompleted ? '-319px -388px' : '-319px -337px'};
  }

  &.transportationicon {
    background-position: ${({ isSelected, isCompleted }) =>
      isSelected ? '-143px -513px' : isCompleted ? '-371px -388px' : '-371px -337px'};
  }

  &.educationicon {
    background-position: ${({ isSelected, isCompleted }) =>
      isSelected ? '-214px -513px' : isCompleted ? '-422px -388px' : '-422px -337px'};
  }

  &.savingsicon {
    background-position: ${({ isSelected, isCompleted }) =>
      isSelected ? '-285px -513px' : isCompleted ? '-475px -388px' : '-475px -337px'};
  }

  &.healthinsuranceicon {
    background-position: ${({ isSelected, isCompleted }) =>
      isSelected ? '-483px -267px' : isCompleted ? '-526px -518px' : '-471px -518px'};
  }

  &.locationicon {
    background-position: -58px -337px;
  }

  &.overspent .totalicon {
    background-position: -502px -443px;
  }

  &.totalicon {
    background-position: -428px -443px;
    height: 70px;
    width: 71px;
    display: inline-block;
    margin: 0 5px !important;
  }
`
const CostCol = styled.div`
  width: 75px;
  float: right;
  font-size: 1.2em;
  height: 20px;
  color: ${({ overspent }) => (overspent ? 'red' : '#059345')};
  font-weight: bold;
`
const SectionLink = styled(Link)`
  clear: both;
  min-height: 30px;
  display: block;
  padding: 12px 7px;
  color: #000;
`
const SummaryText = styled(Text)`
  margin: 0;
  font-size: 18px;
`
const StepBox = styled.div`
  padding: 10px;
  border: 1px solid ${PB90T};
  background-color: ${PB97T};
  border-radius: 4px;
`
const HeaderBar = styled.div`
  background-color: ${SLB50S};
  padding: 5px;
  margin: -9px;
  border-radius: 4px;
  color: ${WHITE};
`
const HorizontalSummary = ({
  budgetId,
  occupation,
  location,
  currentRoundId,
  reverse = false,
  startingSalary,
  rounds,
  responses,
  monthlyTotal
}) => {
  const findBudgetAmountInRound = roundId => {
    const bAmt = responses.find(round => round.round === roundId)

    return formatCurrency((bAmt && bAmt.total) || 0)
  }
  const gapSize = reverse ? '4px' : '10px'

  return (
    <StepBox>
      <HeaderBar>
        <Flex gap="4px">
          <LocationIcon />
          <div>
            {t`Location: `}
            <LocationLink href={`/budget/${budgetId}/location`}>
              {location.name}
            </LocationLink>
          </div>
        </Flex>
        {occupation && (
          <Flex gap="4px" margin="6px 0 0 0">
            <OccupationsIcon />
            <div>
              {t`Occupation: `}
              <LocationLink href={occupation.url}>{occupation.title}</LocationLink>
            </div>
          </Flex>
        )}
      </HeaderBar>
      <Flex gap={gapSize}>
        {reverse && (
          <SectionLink href={`/budget/${budgetId}/reverse`}>
            <Flex alignItems="center" gap={gapSize} direction="column">
              <IconCol className={`earningsicon`} />
              <CenterCol>
                <SummaryText align="center" color={BLACK} weight="bold" size="large">
                  {t`Salary`}
                </SummaryText>
              </CenterCol>
              <CostCol>{fmtCurrency(startingSalary / 12)}</CostCol>
            </Flex>
          </SectionLink>
        )}
        {rounds
          .filter(rnd => rnd.visibleMenu)
          .map(round => (
            <>
              <SectionLink
                href={`/budget/${budgetId}/question/${round.roundId}`}
                key={round.id}
                id={round.id}>
                <Flex alignItems="center" gap={gapSize} direction="column">
                  <IconCol
                    isSelected={round.roundId === currentRoundId}
                    className={`${round.id}icon`}
                  />
                  <CenterCol>
                    <SummaryText color={BLACK} align="center" weight="bold" size="large">
                      <span>{round.name()}</span>
                    </SummaryText>
                  </CenterCol>
                  <CostCol align="center">
                    {findBudgetAmountInRound(round.roundId)}
                  </CostCol>
                </Flex>
              </SectionLink>
            </>
          ))}

        <EqualsIcon />
        <SectionLink href={`/budget/${budgetId}/summary`}>
          <Flex alignItems="center" gap={gapSize} direction="column">
            <IconCol className={`totalicon`} />
            <CenterCol>
              <SummaryText align="center" color={BLACK} weight="bold" size="large">
                {reverse ? t`Unspent` : t`Total`}
              </SummaryText>
              <SummaryText></SummaryText>
            </CenterCol>
            <CostCol overspent={monthlyTotal < 0}>{fmtCurrency(monthlyTotal)}</CostCol>
          </Flex>
        </SectionLink>
      </Flex>
    </StepBox>
  )
}

export { HorizontalSummary }
